import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import EmployeeNavbar from "../Layout/EmployeeNavbar";
import {
  FaTasks,
  FaClock,
  FaFileAlt,
  FaStopwatch,
} from "react-icons/fa";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  Cell,
} from "recharts";

import {
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  subWeeks,
  parseISO,
  isWithinInterval,
} from "date-fns";

import API_BASE_URL from "../config";

const EmployeeDashboard = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState({ name: "Employee" });
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState([]);
  const [filter, setFilter] = useState("thisWeek");
  const [worklogs, setWorklogs] = useState([]);

  const [pendingTasks, setPendingTasks] = useState(0);
  const [pendingLeaves, setPendingLeaves] = useState(0);
  const [remainingLeaves, setRemainingLeaves] = useState(0);
  const [remainingLeaveDetails, setRemainingLeaveDetails] = useState([]);
  const [docCount, setDocCount] = useState(0);
  const [leaveSummary, setLeaveSummary] = useState([]);


  useEffect(() => {
    const token = sessionStorage.getItem("token");
    const employeeId = sessionStorage.getItem("employeeId");
    const roles = JSON.parse(sessionStorage.getItem("roles") || "[]");
    const status = sessionStorage.getItem("status") || "";
    const userId = sessionStorage.getItem("userId");

    if (!token || status.toUpperCase() !== "ACTIVE" || !roles.includes("ROLE_USER")) {
      navigate("/");
      return;
    }

    // Fetch Profile
    fetch(`${API_BASE_URL}/api/users/${userId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => setProfile({ name: data.name || "Employee" }))
      .catch(() => setProfile({ name: "Employee" }));

    // Fetch Worklogs
    fetch(`${API_BASE_URL}/api/worklogs/employee/${employeeId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        setWorklogs(data);
        setChartData(processWorklogData(data, filter));
        setLoading(false);
      });

    // Pending Tasks
    fetch(`${API_BASE_URL}/api/tasks/employee/${employeeId}/pending-count`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => setPendingTasks(data.pendingCount || 0));

    // Pending Leaves
    fetch(`${API_BASE_URL}/api/leaves/pending-count`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((count) => setPendingLeaves(count));

    // NEW API — Remaining Leaves Summary
    fetch(`${API_BASE_URL}/api/admin/leave-summary/${userId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        setRemainingLeaveDetails(data);
        setRemainingLeaves(
          data.reduce((sum, item) => sum + (item.remaining || 0), 0)
        );
      });

    // ✅ Fetch leave summary list
    fetch(`${API_BASE_URL}/api/admin/leave-summary/${userId}`, {
      headers: { Authorization: "Bearer " + token },
    })
      .then((res) => res.json())
      .then((data) => setLeaveSummary(data))
      .catch((err) => console.error("Error fetching leave summary:", err));


    // Documents Count
    fetch(`${API_BASE_URL}/api/uploaddocuments/mydocuments/count`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => setDocCount(data.documentCount || 0));
  }, [navigate]);

  // Update chart when filter changes
  useEffect(() => {
    if (worklogs.length > 0) {
      setChartData(processWorklogData(worklogs, filter));
    }
  }, [filter, worklogs]);

  // Worklog Processing
  const processWorklogData = (worklogs, filterType) => {
    const now = new Date();
    let range;

    if (filterType === "thisWeek") {
      range = { start: startOfWeek(now), end: endOfWeek(now) };
    } else if (filterType === "lastWeek") {
      range = {
        start: startOfWeek(subWeeks(now, 1)),
        end: endOfWeek(subWeeks(now, 1)),
      };
    } else {
      range = { start: startOfMonth(now), end: endOfMonth(now) };
    }

    const dayMap = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const summary = {};

    worklogs.forEach((log) => {
      const logDate = parseISO(log.date);
      if (!isWithinInterval(logDate, range)) return;

      const day = dayMap[logDate.getDay()];
      if (!summary[day])
        summary[day] = { name: day, Tasks: 0, Billable: 0, NonBillable: 0 };

      summary[day].Tasks += 1;

      if (log.billingType === "billable")
        summary[day].Billable += log.hours;
      else summary[day].NonBillable += log.hours;
    });

    const ordered = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    return ordered.map(
      (day) => summary[day] || { name: day, Tasks: 0, Billable: 0, NonBillable: 0 }
    );
  };

  if (loading)
    return <div style={{ textAlign: "center", marginTop: "20%" }}>Loading...</div>;

  // Overview Chart (Pie Data)
  const overviewData = [
    { name: "Tasks Pending", value: pendingTasks },
    { name: "Leave Requests", value: pendingLeaves },
    { name: "Remaining Leaves", value: remainingLeaves },
    { name: "Documents", value: docCount },
  ];

  const COLORS = ["#007bff", "#ff6b6b", "#28a745", "#ffc107"];

  return (
    <div style={styles.container}>
      <div style={{ position: "fixed", top: 0, width: "100%", zIndex: 9999 }}>
        <EmployeeNavbar />
      </div>

      <div style={{ ...styles.content, paddingTop: "130px" }}>
        <h2 style={styles.heading}>👋 Welcome, {profile.name}!</h2>

        {/* Cards */}
        <div style={styles.cardGrid}>
          <DashboardCard
            title="My Tasks"
            subtitle={`${pendingTasks} Pending`}
            icon={<FaTasks size={30} color="#007bff" />}
            path="/viewtasks"
          />
          <DashboardCard
            title="Pending Leave Requests"
            subtitle={`${pendingLeaves} Pending`}
            icon={<FaClock size={30} color="#17a2b8" />}
            path="/allleaves"
          />
          <DashboardCard
            title="Remaining Leaves"
            subtitle={`${remainingLeaves} Leaves`}
            icon={<FaStopwatch size={30} color="#28a745" />}
          />
          <DashboardCard
            title="Documents"
            subtitle={`${docCount} Uploaded`}
            icon={<FaFileAlt size={30} color="#ffc107" />}
            path="/viewdocuments"
          />
        </div>

        {/* 🔥 Overview + Summary in One Div */}
        <div style={{ display: "flex", gap: "20px", width: "100%" }}>

          {/* 🔥 New Overview Chart */}
          <div style={styles.chartCard}>
            <h5 style={{ marginBottom: "15px", color: "#444" }}>📌 Overview Summary</h5>

            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={overviewData}
                  dataKey="value"
                  nameKey="name"
                  outerRadius={120}
                 
                >
                  {overviewData.map((entry, index) => (
                    <Cell key={index} fill={COLORS[index]} />
                  ))}
                </Pie>

                <Tooltip
                  contentStyle={{
                    backgroundColor: "#fff",
                    border: "1px solid #ddd",
                    borderRadius: "8px",
                  }}
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* 📝 Leave Summary Card */}
          <div style={styles.summaryCard}>
            <h5 style={styles.summaryHeader}>Leave Summary</h5>

            {leaveSummary.map((item, index) => (
              <div key={index} style={styles.summaryRow}>
                <span>{item.leaveType}</span>
                <strong>
                  {item.remaining}{" "}
                  <span style={styles.appliedBox}>[{item.applied}]</span>
                </strong>
              </div>
            ))}

            {/* Total Section */}
            <div style={styles.summaryTotalRow}>
              <strong>Total</strong>
              <strong>
                {leaveSummary.reduce((sum, item) => sum + item.remaining, 0)}{" "}
                <span style={styles.appliedBox}>
                  [
                  {leaveSummary.reduce((sum, item) => sum + item.applied, 0)}
                  ]
                </span>
              </strong>
            </div>
          </div>
        </div>



        {/* Weekly Chart */}
        <div style={styles.filterContainer}>
          <label style={{ marginRight: "10px" }}>Filter by:</label>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={styles.filterDropdown}
          >
            <option value="thisWeek">This Week</option>
            <option value="lastWeek">Last Week</option>
          </select>
        </div>

        <div style={styles.chartCard}>
          <h5 style={{ marginBottom: "15px" }}>📊 Weekly Hours</h5>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chartData}>
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="Billable" fill="#28a745" radius={[5, 5, 0, 0]} barSize={30} />
              <Bar dataKey="NonBillable" fill="#ff6b6b" radius={[5, 5, 0, 0]} barSize={30} />
            </BarChart>
          </ResponsiveContainer>
        </div>



      </div>
    </div>
  );
};

// Card Component
const DashboardCard = ({ title, subtitle, icon, path }) => {
  const navigate = useNavigate();

  return (
    <div
      style={styles.card}
      onClick={() => path && navigate(path)}
    >
      <div>
        <h6 style={styles.cardTitle}>{title}</h6>
        <small style={styles.cardSubtitle}>{subtitle}</small>
      </div>
      <div style={styles.cardIcon}>{icon}</div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #eef2f7, #f7f9fc)",
  },
  content: {
    padding: "30px",
  },
  heading: {
    fontWeight: "600",
    color: "#333",
    marginBottom: "25px",
  },
  cardGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: "20px",
    marginBottom: "30px",
  },
  card: {
    background: "white",
    borderRadius: "15px",
    padding: "22px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
    cursor: "pointer",
    transition: "0.25s",
  },
  cardTitle: {
    margin: 0,
    fontWeight: "600",
    color: "#333",
    fontSize: "1.1rem",
  },
  cardSubtitle: {
    color: "#6c757d",
    fontSize: "0.85rem",
  },
  cardIcon: {
    background: "#f1f3f5",
    padding: "12px",
    borderRadius: "50%",
  },
  chartCard: {
    background: "white",
    borderRadius: "15px",
    padding: "20px",
    marginBottom: "30px",
    boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
    flex: 2,
  },
  filterContainer: {
    textAlign: "right",
    marginBottom: "10px",
  },
  filterDropdown: {
    padding: "7px 12px",
    borderRadius: "8px",
    border: "1px solid #ccc",
  },

  summaryCard: {
    background: "white",
    borderRadius: "12px",
    padding: "20px",
    boxShadow: "0px 4px 12px rgba(0,0,0,0.08)",
    marginBottom: "25px",
    border: "1px solid #e5e5e5",
    flex: 1,

  },

  summaryHeader: {
    fontWeight: "600",
    marginBottom: "15px",
    paddingBottom: "8px",
    borderBottom: "1px solid #ddd",
    color: "#333",
  },

  summaryRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "6px 0",
    fontSize: "15px",
    borderBottom: "1px dotted #eee",
  },

  summaryTotalRow: {
    display: "flex",
    justifyContent: "space-between",
    paddingTop: "10px",
    marginTop: "10px",
    borderTop: "2px solid #ccc",
    fontSize: "16px",
  },

  appliedBox: {
    fontSize: "14px",
    color: "#777",
  },

};

export default EmployeeDashboard;
